import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

type LightboxProps = {
  src: string;
  alt: string;
  /** Optional caption shown in the top bar (e.g. "FIG.03 — Data distributions"). */
  label?: string;
  onClose: () => void;
};

/**
 * Photo-album style image viewer: renders a full-screen overlay (portalled to
 * <body> so a transformed ancestor can't trap `position: fixed`) and fits the
 * image to the whole screen with `object-contain`, so the entire image is
 * visible at once — no scrolling. Closes on Esc, backdrop click, or the ✕.
 */
export function Lightbox({ src, alt, label, onClose }: LightboxProps) {
  const { t } = useTranslation("landing");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label ?? alt}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex flex-col bg-black/90 p-3 backdrop-blur-sm sm:p-5"
    >
      <div className="mx-auto flex w-full max-w-[1600px] shrink-0 items-center justify-between gap-4 px-1 pb-3">
        {label ? (
          <span className="mono-label text-[#E2EDE7]">{label}</span>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label={t("figures.close")}
          className="bp-mono text-[12px] uppercase tracking-[0.12em] text-[#788A82] transition-colors hover:text-[#E2EDE7]"
        >
          ✕ {t("figures.close")}
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <img
          src={src}
          alt={alt}
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full rounded-sm object-contain"
        />
      </div>
    </div>,
    document.body,
  );
}

export default Lightbox;
